import { type CSSProperties, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatClock, useCountdown } from '@/features/voting/countdown'
import { cn } from '@/lib/cn'

/**
 * Les briques communes de l'écran de la salle.
 *
 * Toutes les tailles sont en `min(vh, vw)` : la même page doit tenir sur une
 * télé 1920×1080, un vieux 1280×720 et une tablette, sans défilement — une
 * télé accrochée au mur n'a personne pour faire défiler. La hauteur est la
 * contrainte réelle (seize neuvièmes), la largeur ne sert qu'à protéger une
 * tablette en portrait.
 */

/** Grille « colonnes × lignes » pour `count` cartes, du plus lisible au plus serré. */
export function gridFor(count: number, columns = 1): { cols: number; rows: number } {
  // Une seule section occupe toute la largeur ; deux côte à côte (Miss et
  // Master) n'en ont que la moitié chacune : on y met moins de colonnes.
  const wide: Record<number, number> = { 0: 1, 1: 1, 2: 2, 3: 3, 4: 4, 5: 3, 6: 3, 7: 4, 8: 4 }
  const narrow: Record<number, number> = { 0: 1, 1: 1, 2: 2, 3: 2, 4: 2, 5: 3, 6: 3 }
  const table = columns > 1 ? narrow : wide
  const cols = table[count] ?? (columns > 1 ? 3 : Math.min(6, Math.ceil(count / 2)))
  return { cols, rows: Math.max(1, Math.ceil(count / cols)) }
}

/**
 * La photo d'un candidat, ou son numéro en très grand quand il n'en a pas :
 * une case vide sur une télé ressemble à une panne.
 */
export function CandidatePhoto({
  url,
  number,
  name,
  className,
}: {
  url: string | null
  number: number | null
  name: string
  className?: string
}) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        // Le haut du cadre : c'est là qu'est le visage, sur une photo en pied
        // comme sur un portrait.
        className={cn('h-full w-full object-cover object-[50%_20%]', className)}
      />
    )
  }
  return (
    <div
      aria-hidden
      className={cn(
        'flex h-full w-full items-center justify-center bg-gradient-to-br from-[color-mix(in_srgb,var(--champagne-400)_25%,transparent)] via-bg-elevated to-[color-mix(in_srgb,var(--ember-400)_20%,transparent)] font-display text-champagne-300',
        className,
      )}
    >
      <span className="text-[min(14vh,8vw)] leading-none">
        {number !== null ? number : name.slice(0, 1).toUpperCase()}
      </span>
    </div>
  )
}

/**
 * Le décompte du vote sur la télé.
 *
 * Il tourne seul entre deux lectures, corrigé de l'écart d'horloge. À zéro,
 * il dit « temps écoulé » et reste là : le vote est encore ouvert, c'est le
 * MC qui le ferme — et l'encadré disparaît alors avec lui.
 */
export function ScreenCountdown({
  endsAt,
  serverTime,
  compact = false,
}: {
  endsAt: string | null
  serverTime: string
  /** Plusieurs votes à la fois : un décompte par colonne, plus petit. */
  compact?: boolean
}) {
  const { t } = useTranslation()
  const remaining = useCountdown(endsAt, serverTime)
  if (remaining === null) return null

  const size = compact ? 'text-[min(8vh,4.4vw)]' : 'text-[min(13vh,8vw)]'
  if (remaining === 0) {
    return (
      <div className="shrink-0 whitespace-nowrap text-right">
        <p className={cn('font-mono leading-none tabular-nums text-content-muted', size)}>
          {formatClock(0)}
        </p>
        <p
          role="status"
          className={cn(
            'mt-[1vh] font-display leading-none text-champagne-300',
            compact ? 'text-[min(3.2vh,1.9vw)]' : 'text-[min(4.5vh,2.8vw)]',
          )}
        >
          {t('voteTimer.screenElapsed')}
        </p>
      </div>
    )
  }

  return (
    <div className="shrink-0 whitespace-nowrap text-right">
      <p className="text-[min(2.6vh,1.6vw)] text-content-secondary">
        {t('voteTimer.screenRemaining')}
      </p>
      <p
        role="timer"
        aria-live="off"
        className={cn(
          'font-mono leading-none tabular-nums',
          size,
          remaining <= 10 ? 'text-ember-300 motion-safe:animate-pulse' : 'text-content-primary',
        )}
      >
        {formatClock(remaining)}
      </p>
    </div>
  )
}

/**
 * Un texte long (la présentation d'un candidat) qui prend la place qui reste,
 * coupé net à la dernière ligne entière, avec « … ».
 *
 * `line-clamp` seul ne suffit pas : le nombre de lignes disponibles dépend de
 * la carte (six candidats ou deux, une devise sur une ligne ou deux) et de la
 * taille de la télé. On mesure la boîte, on en déduit le nombre de lignes —
 * jamais une demi-ligne coupée à mi-hauteur, jamais de débordement. Sans
 * place pour une seule ligne, le texte se cache plutôt que d'être tronqué.
 */
export function ClampToFit({
  text,
  className,
  style,
}: {
  text: string
  className?: string
  style?: CSSProperties
}) {
  const boxRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLParagraphElement>(null)
  const [lines, setLines] = useState(0)

  useLayoutEffect(() => {
    const box = boxRef.current
    const paragraph = textRef.current
    if (!box || !paragraph) return
    const measure = () => {
      const computed = getComputedStyle(paragraph)
      const lineHeight =
        parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.3 || 0
      if (lineHeight <= 0) return
      setLines(Math.max(0, Math.floor((box.clientHeight + 1) / lineHeight)))
    }
    measure()
    // La télé change de taille (ou la page de candidats) : on remesure.
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    return () => observer.disconnect()
  }, [text])

  return (
    <div ref={boxRef} className="min-h-0 flex-1 overflow-hidden">
      <p
        ref={textRef}
        className={className}
        style={{
          ...style,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: Math.max(lines, 1),
          overflow: 'hidden',
          visibility: lines === 0 ? 'hidden' : undefined,
        }}
      >
        {text}
      </p>
    </div>
  )
}

/** Une carte compacte : photo, numéro, nom. Pas de bouton — la télé ne vote pas. */
export function CompactCandidateCard({
  name,
  number,
  photoUrl,
}: {
  name: string
  number: number | null
  photoUrl: string | null
}) {
  const { t } = useTranslation()
  return (
    <article className="relative flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-lg border border-[color-mix(in_srgb,var(--champagne-400)_40%,transparent)] bg-bg-elevated">
      <div className="relative min-h-0 flex-1">
        <CandidatePhoto url={photoUrl} number={number} name={name} />
        {number !== null ? (
          <span className="absolute left-[0.8vh] top-[0.8vh] rounded-md bg-black/75 px-[1vh] py-[0.3vh] font-mono text-[min(4vh,2.4vw)] font-semibold leading-tight text-champagne-300">
            {t('screen.number', { number })}
          </span>
        ) : null}
      </div>
      {/* Le nom sous la photo, jamais posé dessus : une robe blanche sous
          un texte blanc, et la salle ne lit plus rien. */}
      <p className="truncate px-[1vh] py-[0.9vh] text-center font-display text-[min(3.6vh,2.1vw)] leading-tight text-content-primary">
        {name}
      </p>
    </article>
  )
}
